import React, { useMemo, useState } from 'react';
import { ButtonBar, Button, Dialog } from '@ygtechnologies/rubus-ui';
import { Trans } from '@lingui/react/macro';
import { useParams } from 'react-router';
import { ConfiguratorOptionDto } from '@/services/configurator/configurator.types';
import { useOptionConfigurator } from '../OptionConfiguratorContext';
import { useConfiguratorMutations } from '../ConfiguratorMutationContext';
import { findDependentGroups } from '../dependencyHelpers';
import { DependencyWizardStep } from './DependencyWizardStep';
import theme from './DependencyWizardModal.module.scss';

export interface DependencyWizardModalProps {
    triggerOption: ConfiguratorOptionDto;
    onClose: () => void;
}

export const DependencyWizardModal: React.FC<DependencyWizardModalProps> = ({ triggerOption, onClose }) => {
    const { configuratorData } = useOptionConfigurator();
    const { selectOptions } = useConfiguratorMutations();
    const { roomId } = useParams<{ roomId: string }>();

    const allGroups = useMemo(() => configuratorData?.groups ?? [], [configuratorData]);

    const triggerGroup = useMemo(
        () => allGroups.find((x) => x.roomId === +roomId && x.id === triggerOption.optionGroupId),
        [allGroups, roomId, triggerOption.optionGroupId]
    );

    const dependentGroups = useMemo(
        () => (triggerGroup ? findDependentGroups(allGroups, triggerGroup) : []),
        [allGroups, triggerGroup]
    );

    const [pendingSelections, setPendingSelections] = useState<Record<number, ConfiguratorOptionDto>>(() => {
        const initial: Record<number, ConfiguratorOptionDto> = {};
        dependentGroups.forEach((g) => {
            if (g.selectedOption) {
                initial[g.id] = g.selectedOption;
            }
        });
        return initial;
    });

    const [currentStepIndex, setCurrentStepIndex] = useState(0);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const currentGroup = dependentGroups[currentStepIndex];
    const isLastStep = currentStepIndex === dependentGroups.length - 1;

    // Only include the trigger option + selections from steps already passed by the user.
    // Pre-populated selections from future steps must NOT be used as constraints.
    const knownSelections = useMemo<Record<number, ConfiguratorOptionDto>>(
        () => ({
            ...(triggerGroup ? { [triggerGroup.id]: triggerOption } : {}),
            ...Object.fromEntries(
                dependentGroups
                    .slice(0, currentStepIndex)
                    .flatMap((g) => (pendingSelections[g.id] ? [[g.id, pendingSelections[g.id]]] : []))
            ),
        }),
        [triggerGroup, triggerOption, dependentGroups, currentStepIndex, pendingSelections]
    );

    const handleSelect = (option: ConfiguratorOptionDto) => {
        setPendingSelections((prev) => ({ ...prev, [currentGroup.id]: option }));
    };

    const handleNext = () => {
        setCurrentStepIndex((i) => i + 1);
    };

    const handleBack = () => {
        setCurrentStepIndex((i) => i - 1);
    };

    const handleConfirm = async () => {
        setIsSubmitting(true);
        try {
            const resolvedRoomId = roomId === '0' || !roomId ? null : Number(roomId);
            const selections = [
                { option: triggerOption, customFieldData: null, roomId: resolvedRoomId },
                ...dependentGroups
                    .map((g) => pendingSelections[g.id])
                    .filter((opt): opt is ConfiguratorOptionDto => !!opt)
                    .map((option) => ({ option, customFieldData: null, roomId: resolvedRoomId })),
            ];
            await selectOptions(selections);
            onClose();
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!triggerGroup || !currentGroup) return null;

    const pendingForCurrent = pendingSelections[currentGroup.id];
    const currentSelectionIsInvalid =
        pendingForCurrent !== undefined &&
        pendingForCurrent.dependencyConflicts.some((fw) => knownSelections[fw.groupId]?.id === fw.optionId);

    return (
        <Dialog
            open
            onOpenChange={(open) => {
                if (!open) onClose();
            }}>
            <Dialog.Content contentWidth="lg">
                <Dialog.Header>
                    <div className={theme.header}>
                        <span>{currentGroup.name}</span>
                        {dependentGroups.length > 1 && (
                            <span className={theme.stepCount}>
                                {currentStepIndex + 1} / {dependentGroups.length}
                            </span>
                        )}
                    </div>
                </Dialog.Header>
                <DependencyWizardStep
                    group={currentGroup}
                    triggerGroupName={triggerGroup.name}
                    triggerOptionName={triggerOption.productName}
                    knownSelections={knownSelections}
                    pendingOption={pendingSelections[currentGroup.id] ?? null}
                    onSelect={handleSelect}
                />
                <ButtonBar>
                    {currentStepIndex > 0 && (
                        <Button variant="tertiary" onClick={handleBack} disabled={isSubmitting}>
                            <Trans>Back</Trans>
                        </Button>
                    )}
                    <ButtonBar.Right>
                        <Button variant="tertiary" onClick={onClose} disabled={isSubmitting}>
                            <Trans>Cancel</Trans>
                        </Button>
                        {isLastStep ? (
                            <Button
                                variant="primary"
                                onClick={handleConfirm}
                                loading={isSubmitting}
                                disabled={!pendingSelections[currentGroup.id] || currentSelectionIsInvalid}>
                                <Trans>Confirm</Trans>
                            </Button>
                        ) : (
                            <Button
                                variant="primary"
                                onClick={handleNext}
                                disabled={!pendingSelections[currentGroup.id] || currentSelectionIsInvalid}>
                                <Trans>Next</Trans>
                            </Button>
                        )}
                    </ButtonBar.Right>
                </ButtonBar>
            </Dialog.Content>
        </Dialog>
    );
};
